import { useMemo, useState } from 'react';
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownList,
    DropdownTrigger,
    DropdownTriggerContext,
    Search,
    useMediaQuery,
    type SearchProps,
} from '@bi-digital/react';
import { useTranslation } from 'react-i18next';
import type { FetcherError } from '@bino/utils/fetcher';
import { fetcher } from '@bino/utils/fetcher';
import useSWRImmutable from 'swr/immutable';
import { useSearchParams } from 'react-router';
import classNames from 'classnames';
import { ChevronDownIcon, TogglebuttonIcon } from '@bi-digital/icons';

import { Pending } from '@bino/utils/components/pending';

import type { SearchResponse, SearchResult } from '../types';
import { Result } from '../components/result/Result';
import { Filters } from '../components/filters/Filters';
import { NoResults } from '../components/no-results/NoResults';

import styles from './programme-search.module.css';

export const ProgrammeSearchPage = () => {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sorting, setSorting] = useState<'relevance' | 'alphabetical'>('relevance');
    const [sortOpen, setSortOpen] = useState(false);
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const isCompactScreen = useMediaQuery('(width < 768px)');

    const { data, isLoading } = useSWRImmutable<SearchResponse, FetcherError>(
        () => {
            const params = new URLSearchParams(searchParams);
            params.set('lang', i18n.language);
            return `/api/study-search/?${params.toString()}`;
        },
        fetcher.get<SearchResponse>,
        { keepPreviousData: true, errorRetryCount: 2 },
    );

    const results = useMemo<SearchResult[] | undefined>(() => {
        if (!data || !data.results) return undefined;
        if (sorting === 'alphabetical') {
            return [...data.results].sort((a, b) => a.title.localeCompare(b.title));
        }
        return data.results;
    }, [data, sorting]);

    const handleSort = (newSorting: typeof sorting) => {
        setSorting(newSorting);
        setSortOpen(false);
    };

    const handleSearch: SearchProps['onSubmit'] = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set('q', value);
            return searchParams;
        });
    };

    const resetFilters = () => {
        setSearchParams((s) => ({ q: s.get('q') || [] }));
        setFilterDialogOpen(false);
    };

    const filterCount = useMemo(
        () => Array.from(searchParams.entries()).filter(([key]) => key !== 'q').length,
        [searchParams],
    );

    return (
        <div className={styles.container} data-color-scheme='light' data-color='neutral'>
            <div className={styles.grid}>
                <div className={styles.header}>
                    <h1>{t('programmesTitle')}</h1>
                    {isCompactScreen && (
                        <div className={styles.searchRow}>
                            <Search
                                placeholder={t('placeholder')}
                                defaultValue={searchParams.get('q') || ''}
                                onSubmit={handleSearch}
                                onReset={handleSearch}
                            />
                            <Button
                                onClick={() => setFilterDialogOpen(true)}
                                variant='flat'
                                endIcon={<TogglebuttonIcon aria-hidden='true' />}
                            >
                                {t('filter.toggleButton')}
                            </Button>
                            <Dialog
                                className={styles.filterDrawer}
                                open={filterDialogOpen}
                                onClose={() => setFilterDialogOpen(false)}
                                anchor='bottom'
                            >
                                <DialogCloseButton
                                    aria-label={t('filter.closeButton')}
                                    onClick={() => setFilterDialogOpen(false)}
                                />
                                <DialogHeader>{t('filter.header')}</DialogHeader>
                                <DialogContent>
                                    <Filters type='Programmes' />
                                </DialogContent>
                                <DialogFooter>
                                    <Button className={styles.filterActionButton} variant='text' onClick={resetFilters}>
                                        {t('filter.resetButton')}
                                    </Button>
                                    <Button
                                        className={styles.filterActionButton}
                                        onClick={() => setFilterDialogOpen(false)}
                                    >
                                        {t('filter.applyButton')} ({filterCount})
                                    </Button>
                                </DialogFooter>
                            </Dialog>
                        </div>
                    )}
                </div>
            </div>
            <div className={classNames(styles.grid, styles.content)}>
                <div className={classNames(styles.countContainer, styles.results)}>
                    {data && results && results.length > 0 && (
                        <>
                            <p className={styles.count}>{t(`count.All`, { count: data.count || 0 })}</p>
                            <DropdownTriggerContext onOpenChange={setSortOpen} open={sortOpen}>
                                <DropdownTrigger
                                    className={styles.sorting}
                                    endIcon={<ChevronDownIcon aria-hidden='true' />}
                                >
                                    <span className={styles.sortingLabel}>{t('sort.label')}</span>{' '}
                                    {t(`sort.${sorting}`)}
                                </DropdownTrigger>
                                <Dropdown>
                                    <DropdownList>
                                        <DropdownItem>
                                            <DropdownButton onClick={() => handleSort('relevance')}>
                                                {t('sort.relevance')}
                                            </DropdownButton>
                                        </DropdownItem>
                                        <DropdownItem>
                                            <DropdownButton onClick={() => handleSort('alphabetical')}>
                                                {t('sort.alphabetical')}
                                            </DropdownButton>
                                        </DropdownItem>
                                    </DropdownList>
                                </Dropdown>
                            </DropdownTriggerContext>
                        </>
                    )}
                </div>
                <div className={styles.results}>
                    {results ? (
                        results.length > 0 ? (
                            <div className={styles.list}>
                                {results.map((result) => (
                                    <Result key={result.id} result={result} />
                                ))}
                            </div>
                        ) : (
                            !isLoading && <NoResults type='Programmes' />
                        )
                    ) : (
                        <Pending />
                    )}
                </div>
                {!isCompactScreen && results && (
                    <div className={styles.filters}>
                        <Card>
                            <CardContent>
                                <Search
                                    autoFocus
                                    placeholder={t('placeholder')}
                                    defaultValue={searchParams.get('q') || ''}
                                    onSubmit={handleSearch}
                                    onReset={handleSearch}
                                />
                                <Filters type='Programmes' />
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};
